import type { RumEvent, RumEventDomainContext } from "@datadog/browser-rum";

import { ROUTES_URL } from "@/constant";

import { sanitizeRumError } from "./rum-error-privacy";

type MutableRecord = Record<string, unknown>;

const maskedActionName = "Masked Element";
const redactedUrl = "[REDACTED URL]";
const redactedViewName = "[REDACTED VIEW]";
const urlFieldNames = new Set([
  "url",
  "uri",
  "href",
  "referrer",
  "pathname",
  "search",
  "query",
  "hash",
]);
const modifiableUrlFieldPaths = new Set([
  "view.url",
  "view.referrer",
  "view.performance.lcp.resource_url",
  "error.resource.url",
  "resource.url",
  "long_task.scripts[].source_url",
]);
const redactedTextFieldPaths = new Set([
  "resource.graphql.variables",
  "resource.websocket.close_reason",
  "long_task.scripts[].invoker",
]);
const emptiedObjectFieldPaths = new Set(["resource.request.headers", "resource.response.headers"]);
const userContextFieldPattern =
  /^(?:user|usr|customer|admin|staff|employee|tenant|person|branch|terminal|device|role|features?|modules?|country|channel|section|email|phone|mobile|accountnumber)/;

const routeTemplates = [...new Set(Object.values(ROUTES_URL))]
  .filter((template) => template.startsWith("/") && !/[?#*]/.test(template))
  .map((template) => ({ template, matcher: routeTemplateMatcher(template) }));

function routeTemplateMatcher(template: string): RegExp {
  const pattern = template
    .split("/")
    .map((segment) =>
      /^:[A-Za-z0-9_-]+$/.test(segment) ? "[^/]+" : segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    )
    .join("/");

  return new RegExp(`^${pattern}/?$`);
}

export function normalizeViewName(path: string): string | undefined {
  try {
    let pathname = new URL(path, "https://rum.invalid").pathname;

    for (let attempt = 0; attempt < 4; attempt += 1) {
      const decodedPathname = decodeURIComponent(pathname);
      if (decodedPathname === pathname) {
        break;
      }
      pathname = decodedPathname;
    }

    if (/%[\da-f]{2}/i.test(pathname)) {
      return undefined;
    }

    return routeTemplates.find(({ matcher }) => matcher.test(pathname))?.template;
  } catch {
    return undefined;
  }
}

function sanitizeUrl(value: string): string {
  return normalizeViewName(value) ?? redactedUrl;
}

function sanitizeText(value: string): string | undefined {
  let sanitized = value;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    try {
      const decoded = decodeURIComponent(sanitized);
      if (decoded === sanitized) {
        break;
      }
      sanitized = decoded;
    } catch {
      return undefined;
    }
  }

  if (/%[\da-f]{2}/i.test(sanitized)) {
    return undefined;
  }

  return sanitized
    .replace(/https?:\/\/[^\s"'<>]+/gi, "[URL REDACTED]")
    .replace(/(^|[^\d])(?:\d[ -]?){13,19}(?=$|[^\d])/g, "$1[PAYMENT REDACTED]")
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[EMAIL REDACTED]")
    .replace(/\b[STFGM]\d{7}[A-Z]\b/gi, "[SG ID REDACTED]")
    .replace(/\b\d{6}[ -]?\d{2}[ -]?\d{4}\b/g, "[MY ID REDACTED]")
    .replace(/(?:\+?65[ -]?)?[89]\d{3}[ -]?\d{4}\b/g, "[PHONE REDACTED]")
    .replace(/(?:\+?60[ -]?)?1\d[ -]?\d{3,4}[ -]?\d{4}\b/g, "[PHONE REDACTED]")
    .replace(
      /([?&])((?:otp|code|pin|token|password|secret|nric|mykad|ic_number|phone|email|card|account|payment|amount|customer_name)=)[^&#\s]*/gi,
      "$1$2[REDACTED]",
    );
}

function isRecord(value: unknown): value is MutableRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function sanitizeRecord(record: MutableRecord, seen: WeakSet<object>, path = ""): boolean {
  if (seen.has(record)) {
    return false;
  }
  seen.add(record);

  for (const [key, value] of Object.entries(record)) {
    const normalizedKey = key.toLowerCase().replace(/[_-]/g, "");
    const fieldPath = path ? `${path}.${key}` : key;
    if (fieldPath === "view") {
      continue;
    }
    if (emptiedObjectFieldPaths.has(fieldPath)) {
      if (!isRecord(value)) {
        return false;
      }
      record[key] = {};
      continue;
    }
    if (redactedTextFieldPaths.has(fieldPath)) {
      if (typeof value !== "string") {
        return false;
      }
      record[key] = "[REDACTED DATA]";
      continue;
    }
    if (normalizedKey === "context") {
      record[key] = {};
      continue;
    }

    if (
      urlFieldNames.has(normalizedKey) ||
      /(?:url|uri|href|referrer|pathname|search|query|hash)$/.test(normalizedKey)
    ) {
      if (typeof value !== "string" || !modifiableUrlFieldPaths.has(fieldPath)) {
        return false;
      }
      record[key] = sanitizeUrl(value);
      continue;
    }

    if (userContextFieldPattern.test(normalizedKey) || normalizedKey === "account") {
      return false;
    }
    if (path === "action.target" && key !== "name") {
      return false;
    }
    if ((fieldPath === "error.causes" || fieldPath === "error.threads") && value !== undefined) {
      return false;
    }

    if (typeof value === "string") {
      const sanitized = sanitizeText(value);
      if (sanitized === undefined) {
        return false;
      }
      record[key] = sanitized;
      continue;
    }

    if (Array.isArray(value)) {
      if (!sanitizeArray(value, seen, fieldPath)) {
        return false;
      }
      continue;
    }

    if (isRecord(value)) {
      if (!sanitizeRecord(value, seen, fieldPath)) {
        return false;
      }
      continue;
    }

    if (
      value !== null &&
      value !== undefined &&
      typeof value !== "number" &&
      typeof value !== "boolean"
    ) {
      return false;
    }
  }

  return true;
}

function sanitizeArray(values: unknown[], seen: WeakSet<object>, path: string): boolean {
  if (seen.has(values)) {
    return false;
  }
  seen.add(values);

  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (typeof value === "string") {
      if (/(?:url|uri|href|referrer|pathname|search|query|hash)$/i.test(path)) {
        return false;
      }
      const sanitized = sanitizeText(value);
      if (sanitized === undefined) {
        return false;
      }
      values[index] = sanitized;
    } else if (Array.isArray(value)) {
      if (!sanitizeArray(value, seen, `${path}[]`)) {
        return false;
      }
    } else if (isRecord(value)) {
      if (!sanitizeRecord(value, seen, `${path}[]`)) {
        return false;
      }
    } else if (
      value !== null &&
      value !== undefined &&
      typeof value !== "number" &&
      typeof value !== "boolean"
    ) {
      return false;
    }
  }

  return true;
}

export function scrubRumEvent(
  event: RumEvent,
  _context?: RumEventDomainContext,
  viewPrefix?: string,
): boolean {
  try {
    const mutableEvent = event as unknown as MutableRecord;
    if (mutableEvent.type === "error") {
      if (!sanitizeRumError(mutableEvent.error, sanitizeUrl)) {
        return false;
      }
    }

    const view = isRecord(mutableEvent.view) ? mutableEvent.view : undefined;

    if (view) {
      const path =
        typeof view.url === "string" ? view.url : typeof view.name === "string" ? view.name : "";
      const viewName = normalizeViewName(path);

      view.name = viewName
        ? viewPrefix
          ? `${viewPrefix}:${viewName}`
          : viewName
        : redactedViewName;
      view.url = viewName ?? redactedUrl;
      if (typeof view.referrer === "string") {
        view.referrer = sanitizeUrl(view.referrer);
      }
      if (isRecord(view.context)) {
        view.context = {};
      }
      const performance = isRecord(view.performance) ? view.performance : undefined;
      const lcp = performance && isRecord(performance.lcp) ? performance.lcp : undefined;
      if (lcp && typeof lcp.resource_url === "string") {
        lcp.resource_url = sanitizeUrl(lcp.resource_url);
      }
    }

    if (mutableEvent.type === "action" && isRecord(mutableEvent.action)) {
      const action = mutableEvent.action;
      if (action.type === "custom") {
        return false;
      }
      action.name = maskedActionName;
      if (isRecord(action.target)) {
        if (Object.keys(action.target).some((key) => key !== "name")) {
          return false;
        }
        action.target.name = maskedActionName;
      }
    }

    return sanitizeRecord(mutableEvent, new WeakSet());
  } catch {
    return false;
  }
}
